var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import moment from 'moment-timezone';
import { useCallback } from 'react';
import { v4 as uuid } from 'uuid';
import { flowsApi } from 'apis/flows';
import { useEditorContext } from 'flow-builder/editor.context';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { getExceptionsFromHolidays } from 'utils/holidays';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useMigrateOpeningHoursContext } from '../migrateOpeningHours.context';
import { createBusinessHoursNode } from '../utils/createBusinessHoursNode';
export function useMigrateSchedule(props) {
    const { node, replaceNode, triggerClose } = props;
    const { schedule, announcement, holidays, name, timezone } = useMigrateOpeningHoursContext();
    const { state: { setSelectState }, } = useEditorContext();
    const notify = useNotifications();
    const [createSchedule, { isLoading }] = flowsApi.useCreateFlowScheduleMutation();
    const agentName = useAppSelector((state) => state.settings.profile.profile.name);
    const dispatch = useAppDispatch();
    const handleCreateSchedule = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        try {
            const holidaysResponse = (holidays === null || holidays === void 0 ? void 0 : holidays.countryCode)
                ? yield dispatch(flowsApi.endpoints.readFlowsSchedulesHolidays.initiate({
                    country_code: holidays.countryCode,
                    timezone,
                }))
                : null;
            const exceptions = holidaysResponse
                ? [
                    ...getExceptionsFromHolidays({
                        agentName,
                        holidays: (_a = holidaysResponse.data) === null || _a === void 0 ? void 0 : _a.data.holidays,
                        isClosed: !!(holidays === null || holidays === void 0 ? void 0 : holidays.isClosedOnHolidays),
                    }),
                ]
                : [];
            const openingHours = schedule.map(({ date, intervals }) => {
                const localDate = moment.utc();
                const parsedDate = moment.utc(date);
                const dayOfWeek = parsedDate.day() || 7;
                const today = moment.utc().isoWeekday();
                const momentWeekday = moment.utc().isoWeekday(dayOfWeek);
                if (today >= dayOfWeek) {
                    momentWeekday.add(1, 'weeks');
                }
                return {
                    created_at: localDate.toISOString(),
                    created_by: agentName,
                    duration: 1,
                    id: uuid(),
                    intervals,
                    name: parsedDate.format('dddd'),
                    opening_hour_type: 'regular',
                    recurring: {
                        day_of_week: dayOfWeek,
                    },
                    start_date: momentWeekday.toISOString(),
                };
            });
            const res = yield createSchedule({
                announcement: announcement,
                created_at: moment.utc().toISOString(),
                country_code: (_b = holidays === null || holidays === void 0 ? void 0 : holidays.countryCode) !== null && _b !== void 0 ? _b : null,
                name,
                timezone,
                opening_hours: [...openingHours, ...exceptions],
            });
            const isSuccess = 'data' in res;
            if (!isSuccess) {
                notify({
                    title: 'An error occurred when trying to create schedule.',
                }, NotificationType.Error);
                return;
            }
            const newNode = createBusinessHoursNode(node, res.data.id);
            replaceNode(newNode);
            triggerClose();
            setSelectState({ node: newNode });
        }
        catch (error) {
            console.warn(error);
            notify({
                title: 'An error occurred when trying to create schedule.',
            }, NotificationType.Error);
        }
    }), [
        agentName,
        announcement,
        holidays,
        name,
        node,
        timezone,
        schedule,
        createSchedule,
        dispatch,
        notify,
        replaceNode,
        setSelectState,
        triggerClose,
    ]);
    return [handleCreateSchedule, isLoading];
}
